/**
 * Query understanding shared by the federated search and /search (KFT-4145):
 * constraint phrases that keyword search can't satisfy become /search filters,
 * and are stripped from the text so they don't drag unrelated keyword matches
 * in. The output uses the /search filter shape (`priceMax` in euros, `dates`
 * as a `YYYY-MM-DD` pair), so both surfaces apply the same filter through
 * `parseFiltersToTypesenseParams`, and the dropdown's submit can put it in the
 * URL as a visible, removable filter.
 *
 * Only phrases with a /search filter are parsed. "für 20 personen" and
 * "abends" stay plain text until /search has group-size and time-of-day
 * filters. Fixed phrases with a static filter (online, zuhause, …) belong in
 * cluster curation rules instead.
 */

export interface SearchIntent {
  /** Query text with the recognised phrases removed ('' when nothing is left). */
  text: string
  /** /search `priceMax` filter, in euros. */
  priceMax?: number
  /** /search `dates` filter: first and last day, `YYYY-MM-DD`, local time. */
  dates?: [string, string]
}

// `(?:^|\s)…(?=\s|$)` instead of `\b`: JS word boundaries don't treat umlauts
// as word characters ("für", "günstig").
const phrase = (body: string) =>
  new RegExp(`(?:^|\\s)(?:${body})(?=\\s|$)`, 'i')

const PRICE = phrase(
  '(?:unter|bis|max(?:imal)?\\.?|höchstens|weniger als|under|below|up to|less than)\\s*(\\d{1,4})\\s*(?:€|euro|eur)'
)
const CHEAP = phrase(
  'günstig(?:e|es|er)?|billig(?:e|es|er)?|preiswert(?:e|es|er)?|cheap|affordable'
)
const WEEKEND = phrase(
  '(?:am|übers|this|on the|at the|on|at)?\\s*(?:wochenende|weekend)'
)

const strip = (text: string, pattern: RegExp) =>
  text.replace(new RegExp(pattern.source, 'gi'), ' ')

const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/**
 * The coming weekend as a /search date range: Saturday–Sunday, or the rest of
 * it when today is already Saturday (→ today–Sunday) or Sunday (→ today).
 */
const nextWeekend = (now: Date): [string, string] => {
  const day = now.getDay() // 0 = Sunday … 6 = Saturday
  const start = new Date(now)
  if (day !== 0 && day !== 6) start.setDate(now.getDate() + (6 - day))
  const end = new Date(start)
  if (start.getDay() === 6) end.setDate(start.getDate() + 1)
  return [isoDay(start), isoDay(end)]
}

export const parseSearchIntent = (
  query: string,
  now: Date = new Date()
): SearchIntent => {
  let text = query
  const intent: Omit<SearchIntent, 'text'> = {}

  const price = query.match(PRICE)
  if (price) {
    intent.priceMax = Number(price[1])
    // "günstig unter 50 euro": the adjective is now the filter, not a keyword.
    text = strip(strip(text, PRICE), CHEAP)
  }

  if (WEEKEND.test(query)) {
    intent.dates = nextWeekend(now)
    text = strip(text, WEEKEND)
  }

  return { text: text.replace(/\s+/g, ' ').trim(), ...intent }
}

/**
 * /search URL params for a query typed into the federated search: the text
 * as `query`, and each recognised constraint as the filter /search reads
 * (`priceMin`/`priceMax`, `dates` twice), so it shows up as a removable filter
 * and the list matches what the dropdown showed.
 */
export const searchPageQueryParams = (
  query: string,
  now: Date = new Date()
): Record<string, string | string[]> => {
  const { text, priceMax, dates } = parseSearchIntent(query, now)
  return {
    ...(text ? { query: text } : {}),
    resetFilters: 'true',
    ...(priceMax != null ? { priceMin: '0', priceMax: String(priceMax) } : {}),
    ...(dates ? { dates: [...dates] } : {}),
  }
}
